/**
 * テナントのタイムゾーン（02 §11 #4「保存は UTC・表示はテナントの TZ」・03 §20.4「1 テナント 1 つ」）。
 *
 * これまで `'Asia/Tokyo'` の既定が 9 モジュールに散っていたので、既定値と `settings.timezone` の読み取りをここに 1 つだけ置く。
 * 依存は増やさない（`Intl` だけ）。サーバ（`internal/`）でもブラウザ（`ui/`）でも同じものを使う。
 *
 * 壁時計 ⇄ UTC の変換や RRULE の展開のような「計算」は calendar の `@saas/module-calendar/api`（`calendar.time.ts`）が持つ。
 * ここに置くのは **既定値と設定の読み取りだけ**（calendar は基盤モジュールではないので、他モジュールから import できない）。
 */
export const DEFAULT_TIME_ZONE = 'Asia/Tokyo';

const validated = new Map<string, boolean>();

/** IANA のタイムゾーン名として使えるか（`Intl` が受け付けるか）。結果は覚えておく */
export function isValidTimeZone(tz: unknown): tz is string {
  if (typeof tz !== 'string' || tz === '') return false;
  const cached = validated.get(tz);
  if (cached !== undefined) return cached;
  let ok = false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    ok = true;
  } catch {
    ok = false;
  }
  validated.set(tz, ok);
  return ok;
}

/**
 * `tenants.settings.timezone` → 有効な IANA 名。未設定・不正なら `DEFAULT_TIME_ZONE`。
 * 引数は `TenantSummary['settings']`（`Record<string, unknown>`）をそのまま渡せる形にしてある。
 */
export function tenantTimeZone(settings: { timezone?: unknown } | null | undefined): string {
  const tz = settings?.timezone;
  return isValidTimeZone(tz) ? tz : DEFAULT_TIME_ZONE;
}

export interface ZonedWallClock {
  year: number;
  /** 1〜12 */
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let f = formatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', { timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    formatters.set(timeZone, f);
  }
  return f;
}

/** UTC の瞬間 → その TZ の壁時計（calendar の `utcToWall` と同じ結果） */
export function wallClockInZone(date: Date, timeZone: string): ZonedWallClock {
  const parts = formatterFor(isValidTimeZone(timeZone) ? timeZone : DEFAULT_TIME_ZONE).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes): number => Number(parts.find((p) => p.type === type)?.value ?? 0);
  // hourCycle h23 でも一部の環境が "24" を返すので丸める
  return { year: get('year'), month: get('month'), day: get('day'), hour: get('hour') % 24, minute: get('minute'), second: get('second') };
}

/** その TZ での「時」（0〜23）。まとめ送りのジョブが「いま 8 時台のテナント」を拾うのに使う */
export function hourInZone(date: Date, timeZone: string): number {
  return wallClockInZone(date, timeZone).hour;
}

/** その TZ の暦日 'YYYY-MM-DD' */
export function isoDateInZone(date: Date, timeZone: string): string {
  const w = wallClockInZone(date, timeZone);
  return `${w.year}-${pad2(w.month)}-${pad2(w.day)}`;
}

/** 'YYYY-MM-DD HH:mm'（通知文・メール本文・ログ用。calendar の `formatInZone` と同じ形） */
export function formatInZone(date: Date, timeZone: string, opts: { seconds?: boolean } = {}): string {
  const w = wallClockInZone(date, timeZone);
  const base = `${w.year}-${pad2(w.month)}-${pad2(w.day)} ${pad2(w.hour)}:${pad2(w.minute)}`;
  return opts.seconds ? `${base}:${pad2(w.second)}` : base;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}
